import { useCallback, useEffect, useRef } from "react";
import { toast } from "react-toastify";
import { CustomToast } from "../ui/CustomToast";
import { SSEManagerClass } from "../../utils/useSSEEvent";
import { accountsApi, campaignsApi, BASE_API_URL } from "../../api";
import { useAppDispatch, useAppSelector } from "../../hooks";
import {
  AccountPlanStatusData,
  AnalysisData,
  AnalysisStatus,
  GenerationData,
  ImportDataStatus,
  MismatchData,
  RecalculationData,
  SSEDataTypes,
  SSEEvents,
  ToastMessage,
} from "../../types/companyTypes";
import {
  setImportDataStatus,
  setMismatchData,
  setIsFetchTableMembers,
  setRecalculatingStatus,
  setStatus,
  setUpdatedCompanies,
  setUpdatedStatus,
} from "../../features/companySlice";
import { useParams } from "react-router-dom";
import { CampaignId, UpdatedAccountListItem } from "../../types";

const sseManager = SSEManagerClass.getInstance();

export const SSEManager = () => {
  const { campaign_id } = useParams();

  const campaignIdRef = useRef(campaign_id);
  const dispatch = useAppDispatch();
  const token = useAppSelector((state) => state.auth.accessToken) as string;
  const refreshToken = useAppSelector(
    (state) => state.auth.refreshToken,
  ) as string;

  useEffect(() => {
    campaignIdRef.current = campaign_id;
  }, [campaign_id]);

  const generateHandler = (event: SSEEvents, data: GenerationData) => {
    dispatch(setStatus({ [event]: data }));

    const { status, message } = data;

    if (status === "failed") {
      toast.error(
        <CustomToast
          type={"error"}
          text={
            message ||
            "Something went wrong during the analysis of your data. Please try again"
          }
          title={"Analysis failed"}
        />,
      );
    }
  };
  const analysisHandler = (event: SSEEvents, data: AnalysisData) => {
    if (data.campaign_id !== campaignIdRef.current) return;

    const { status, message, progress, account_id, item, name } = data;

    dispatch(setStatus({ [event]: data }));
    let updatedData: UpdatedAccountListItem = {
      status,
      progress,
      id: account_id,
      company: name,
      message,
    };

    if (item) {
      updatedData = { ...updatedData, ...item };
    }

    dispatch(setUpdatedCompanies(updatedData));

    if ([AnalysisStatus.failed, AnalysisStatus.completed].includes(status)) {
      dispatch(
        campaignsApi.util.invalidateTags([
          "Campaign",
          "summary",
          "company",
          "accounts-for-scraping",
          "activities",
          "role-members",
        ]),
      );
      dispatch(setIsFetchTableMembers(true));
      dispatch(accountsApi.util.invalidateTags(["analytics"]));
    }
  };

  const scrapingHandler = (event: SSEEvents, data: AnalysisData) => {
    if (data.campaign_id !== campaignIdRef.current) return;

    const { status, message, progress, account_id, item, name } = data;

    dispatch(setStatus({ [event]: data }));
    let updatedData: UpdatedAccountListItem = {
      [event]: status,
      progress,
      id: account_id,
      company: name,
      message,
    };

    if (item) {
      updatedData = { ...updatedData, ...item };
    }

    dispatch(setUpdatedCompanies(updatedData));
    dispatch(setStatus({ [event]: data }));

    if (
      [
        AnalysisStatus.failed,
        AnalysisStatus.completed,
        AnalysisStatus.completed_no_data,
      ].includes(status)
    ) {
      dispatch(
        campaignsApi.util.invalidateTags([
          "summary",
          "role-members",
          "activities",
          "company",
        ]),
      );
      dispatch(setIsFetchTableMembers(true));
    }
  };

  const recalculationStatusHandler = (
    event: SSEEvents,
    data: RecalculationData,
  ) => {
    if (data.campaign_id !== campaignIdRef.current) return;
    const { status, message } = data;

    if (status === AnalysisStatus.failed) {
      toast.error(
        <CustomToast
          type={"error"}
          text={message || "Something went wrong. Please try again"}
          title={"Recalculating scores failed"}
        />,
      );
      dispatch(setRecalculatingStatus("stop"));
    } else if (
      [AnalysisStatus.completed, AnalysisStatus.completed_no_data].includes(
        status,
      )
    ) {
      dispatch(setRecalculatingStatus("stop"));
    }
  };

  const mismatchHandler = (event: SSEEvents, data: MismatchData) => {
    if (data.campaign_id !== campaignIdRef.current) return;
    dispatch(setMismatchData(data));

    if (!data.mismatches.is_syncing) {
      dispatch(accountsApi.util.invalidateTags(["list-view", "analytics"]));
      dispatch(setIsFetchTableMembers(true));
      dispatch(
        campaignsApi.util.invalidateTags([
          "role-members",
          "company",
          "summary",
          "Campaign",
        ]),
      );
    }
  };

  const importDataHandler = (event: SSEEvents, data: ImportDataStatus) => {
    dispatch(setImportDataStatus(data));
  };

  const accountPlanHandler = (
    event: SSEEvents,
    data: AccountPlanStatusData,
  ) => {
    if (data.campaign_id !== campaignIdRef.current) return;

    dispatch(setStatus({ [event]: data }));
    dispatch(setUpdatedStatus({ [data.account_id]: data.status }));

    dispatch(accountsApi.util.invalidateTags(["list-view"]));
    dispatch(setIsFetchTableMembers(true));
    dispatch(
      campaignsApi.util.invalidateTags([
        "company",
        "summary",
        "Campaign",
        "activities",
      ]),
    );
  };

  const toastMessageHandler = (event: SSEEvents, data: ToastMessage) => {
    if (data.campaign_id !== campaignIdRef.current) return;

    try {
      const { title, description, type } = data;

      toast[type](<CustomToast type={type} title={title} text={description} />);
    } catch (_) {}
  };

  const usersActivitiesHandler = (event: SSEEvents, data: CampaignId) => {
    if (data.campaign_id !== campaignIdRef.current) return;
    dispatch(campaignsApi.util.invalidateTags(["activities"]));
  };

  const playbookHandler = (event: SSEEvents, data: AccountPlanStatusData) => {
    if (data.campaign_id !== campaignIdRef.current) return;

    dispatch(setUpdatedStatus({ [data.member_id]: data.status }));

    dispatch(setIsFetchTableMembers(true));
    dispatch(
      campaignsApi.util.invalidateTags([
        "activities",
        "role-members",
        "company",
      ]),
    );
  };

  const manager = {
    [SSEEvents.analysis_status]: analysisHandler,
    [SSEEvents.scraping_status]: scrapingHandler,
    [SSEEvents.scraping_update_status]: scrapingHandler,
    [SSEEvents.generation_status]: generateHandler,
    [SSEEvents.recalculation_status]: recalculationStatusHandler,
    [SSEEvents.mismatch_update]: mismatchHandler,
    [SSEEvents.import_data_status]: importDataHandler,
    [SSEEvents.account_plan_status]: accountPlanHandler,
    [SSEEvents.campaign_user_stats]: usersActivitiesHandler,
    [SSEEvents.sdr_playbook_status]: playbookHandler,
    [SSEEvents.toast_message]: toastMessageHandler,
  };

  const handleMessage = (sseData: SSEDataTypes) => {
    const { event, data } = sseData;

    const eventHandler = manager[event] as (
      event: SSEEvents,
      data: unknown,
    ) => void;
    eventHandler(event, data);
  };

  const reconnectOnVisible = useCallback(() => {
    if (document.visibilityState === "visible") {
      const last_notification_id = localStorage.getItem("last_notification_id");
      const query = last_notification_id
        ? `?last_notification_id=${last_notification_id}`
        : "";

      sseManager.connect({
        url: `${BASE_API_URL}/me/notifications${query}`,
        token,
        refreshToken,
        onMessage: handleMessage,
        onError: () => {},
        dispatch: dispatch,
      });
    }
  }, [token, refreshToken]);

  useEffect(() => {
    document.addEventListener("visibilitychange", reconnectOnVisible);

    return () => {
      document.removeEventListener("visibilitychange", reconnectOnVisible);
      localStorage.removeItem("last_notification_id");
    };
  }, [reconnectOnVisible]);

  useEffect(() => {
    if (!sseManager.isConnected) {
      const last_notification_id = localStorage.getItem("last_notification_id");
      const query = last_notification_id
        ? `?last_notification_id=${last_notification_id}`
        : "";

      sseManager.connect({
        url: `${BASE_API_URL}/me/notifications${query}`,
        token,
        refreshToken,
        onMessage: handleMessage,
        onError: () => {},
        dispatch: dispatch,
      });
    }
  }, []);

  return null;
};
